import React, { useState, useEffect, useRef, useContext } from "react";
import { NavLink, useLocation, Link } from "react-router-dom";
import { FiHome, FiGrid, FiBookOpen, FiUser, FiMoreHorizontal, FiInfo, FiMail, FiAward, FiLayout, FiVideo, FiX } from "react-icons/fi";
import { motion, AnimatePresence } from "framer-motion";
import { AuthContext } from "../../contexts/AuthContext";

const navItems = [
  { to: "/", icon: FiHome, label: "Home" },
  { to: "/programs", icon: FiGrid, label: "Programs" },
  { to: "/blogs", icon: FiBookOpen, label: "Blogs" },
  { to: "/profile", icon: FiUser, label: "Profile" },
];

const moreMenuItems = [
  { to: "/courses", icon: FiVideo, label: "Pre-recorded Courses" },
  { to: "/about-us", icon: FiInfo, label: "About Us" },
  { to: "/contact-us", icon: FiMail, label: "Contact Us" },
  { to: "/90-day-challenge", icon: FiAward, label: "90 Day Challenge" },
  { to: "/dashboard", icon: FiLayout, label: "My Programs & Purchases" },
];

const BottomNav = () => {
  const location = useLocation();
  const { user } = useContext(AuthContext);
  const [visible, setVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [moreOpen, setMoreOpen] = useState(false);
  const moreRef = useRef(null);

  const filteredNavItems = user ? navItems : navItems.filter(item => item.to !== '/profile');
  const filteredMoreMenuItems = user ? moreMenuItems : moreMenuItems.filter(item => item.to !== '/dashboard');

  const isMoreActive = filteredMoreMenuItems.some(
    (item) => location.pathname === item.to || location.pathname.startsWith(item.to)
  );

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      if (currentScrollY > lastScrollY && currentScrollY > 100) {
        setVisible(false);
      } else {
        setVisible(true);
      }
      setLastScrollY(currentScrollY);
      if (moreOpen) setMoreOpen(false);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [lastScrollY, moreOpen]);

  // Close on route change
  useEffect(() => {
    setMoreOpen(false);
  }, [location.pathname]);

  return (
    <motion.nav
      initial={{ y: 0 }}
      animate={{ y: visible ? 0 : 100 }}
      transition={{ duration: 0.3, ease: "easeInOut" }}
      className="fixed bottom-0 left-0 right-0 z-40 md:hidden glass border-t border-[var(--color-border-default)]"
      aria-label="Bottom navigation"
    >
      {/* More Menu Popover */}
      <AnimatePresence>
        {moreOpen && (
          <>
            {/* Backdrop — covers the page above the nav only, so it doesn't
                dim the nav itself (which would crush contrast on the muted
                labels in light mode where the nav background is translucent
                white). */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-x-0 top-0 bottom-16 bg-black/40 z-[-1]"
              onClick={() => setMoreOpen(false)}
            />
            {/* Menu */}
            <motion.div
              ref={moreRef}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              transition={{ duration: 0.25, ease: "easeOut" }}
              className="absolute bottom-full right-2 mb-2 w-56 glass rounded-xl border border-[var(--color-border-default)] shadow-2xl overflow-hidden"
            >
              <div className="py-2">
                {filteredMoreMenuItems.map((item) => {
                  const isActive = location.pathname === item.to || location.pathname.startsWith(item.to);
                  return (
                    <Link
                      key={item.to}
                      to={item.to}
                      className={`flex items-center gap-3 px-4 py-3 transition-colors duration-200 ${
                        isActive
                          ? "text-brand-500 bg-brand-500/10"
                          : "text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] hover:bg-white/5"
                      }`}
                    >
                      <item.icon className="w-5 h-5 flex-shrink-0" />
                      <span className="text-sm font-medium">{item.label}</span>
                    </Link>
                  );
                })}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      <div className="flex items-center justify-around h-16 max-w-lg mx-auto px-2">
        {filteredNavItems.map((item) => {
          const isActive = location.pathname === item.to ||
            (item.to !== "/" && location.pathname.startsWith(item.to));

          return (
            <NavLink
              key={item.to}
              to={item.to}
              className="flex flex-col items-center justify-center w-full h-full relative focus-ring rounded-lg"
              aria-label={item.label}
            >
              {isActive && (
                <motion.div
                  layoutId="bottom-nav-indicator"
                  className="absolute -top-0 w-8 h-0.5 bg-brand-500 rounded-full"
                  transition={{ type: "spring", stiffness: 300, damping: 30 }}
                />
              )}
              <item.icon
                className={`w-5 h-5 transition-colors duration-200 ${
                  isActive ? "text-brand-500" : "text-[var(--color-text-muted)]"
                }`}
              />
              <span
                className={`mt-1 text-[10px] font-medium transition-colors duration-200 ${
                  isActive ? "text-brand-500" : "text-[var(--color-text-muted)]"
                }`}
              >
                {item.label}
              </span>
            </NavLink>
          );
        })}

        {/* More Button */}
        <button
          onClick={() => setMoreOpen((prev) => !prev)}
          className="flex flex-col items-center justify-center w-full h-full relative focus-ring rounded-lg"
          aria-label="More"
          aria-expanded={moreOpen}
        >
          {isMoreActive && !moreOpen && (
            <motion.div
              layoutId="bottom-nav-indicator"
              className="absolute -top-0 w-8 h-0.5 bg-brand-500 rounded-full"
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
            />
          )}
          {moreOpen ? (
            <FiX
              className="w-5 h-5 transition-colors duration-200 text-brand-500"
            />
          ) : (
            <FiMoreHorizontal
              className={`w-5 h-5 transition-colors duration-200 ${
                isMoreActive ? "text-brand-500" : "text-[var(--color-text-muted)]"
              }`}
            />
          )}
          <span
            className={`mt-1 text-[10px] font-medium transition-colors duration-200 ${
              isMoreActive || moreOpen ? "text-brand-500" : "text-[var(--color-text-muted)]"
            }`}
          >
            More
          </span>
        </button>
      </div>
    </motion.nav>
  );
};

export default BottomNav;
